import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMail, FiMapPin, FiClock, FiSend, FiCheck } = FiIcons;

const AIRTABLE_BASE_ID = 'appsGDZbeW4o9Xz0Q';
const AIRTABLE_TABLE_NAME = 'Contact Requests';
const AIRTABLE_API_TOKEN = 'patczzIhIjLkjqIsa.cd81604b3d573cdf8cd2105cbe67938567153240b25edcc0e61cdc807922e67c';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const services = [
    'Electrical Installation & Maintenance',
    'Electrician Services',
    'CCTV Installation & Repairing',
    'Smart Home Automation',
    'Solar Power Systems',
    'Inverter & UPS Services',
    'Access Control & Security Systems',
    'Networking & Structured Cabling',
    'Electrical Appliance Repair',
    'Annual Maintenance Contracts (AMC)',
    'Custom Electrical & Automation Solutions'
  ];

  const contactInfo = [
    {
      icon: FiPhone,
      title: 'Phone & WhatsApp',
      details: ['+91 99066 69918'],
      action: 'Call Now',
      link: 'tel:+919906669918',
      color: 'text-green-400'
    },
    {
      icon: FiMail,
      title: 'Email Address',
      details: ['tayyabelectricstudio@outlook.com'],
      action: 'Send Email',
      link: 'mailto:tayyabelectricstudio@outlook.com',
      color: 'text-blue-400'
    },
    {
      icon: FiMapPin,
      title: 'Location',
      details: ['Fatehgarh, Baramulla', 'Jammu & Kashmir, India'],
      action: 'Get Directions',
      link: 'https://maps.google.com',
      color: 'text-red-400'
    },
    {
      icon: FiClock,
      title: 'Working Hours',
      details: ['24/7 Emergency Service', 'Mon-Sun: Always Available'],
      action: 'Emergency Call',
      link: 'tel:+919906669918',
      color: 'text-purple-400'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const submitToAirtable = async (data) => {
    const response = await fetch(
      `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${encodeURIComponent(AIRTABLE_TABLE_NAME)}`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AIRTABLE_API_TOKEN}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fields: {
            Name: data.name,
            Phone: data.phone,
            Service: data.service,
            Message: data.message,
            Timestamp: new Date().toISOString(),
            Source: 'Tayyab Electric Studio Website'
          }
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData?.error?.message || 'Airtable submission failed');
    }

    return response.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Submit to Airtable
      await submitToAirtable(formData);

      // On success, open WhatsApp with the message
      const whatsappMessage = `New Service Request from Website:\n\nName: ${formData.name}\nPhone: ${formData.phone}\nService: ${formData.service}\nMessage: ${formData.message}`;
      const whatsappUrl = `https://wa.me/919906669918?text=${encodeURIComponent(whatsappMessage)}`;
      window.open(whatsappUrl, '_blank');

      setIsSubmitted(true);
      setFormData({ name: '', phone: '', service: '', message: '' });
      setTimeout(() => setIsSubmitted(false), 5000);

    } catch (err) {
      console.error('Submission error:', err);
      setError('Something went wrong. Please try WhatsApp directly.');

      // Fallback to WhatsApp
      const whatsappMessage = `Service Request:\n\nName: ${formData.name}\nPhone: ${formData.phone}\nService: ${formData.service}\nMessage: ${formData.message}`;
      const whatsappUrl = `https://wa.me/919906669918?text=${encodeURIComponent(whatsappMessage)}`;
      window.open(whatsappUrl, '_blank');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-blue-900/20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-40 h-40 bg-blue-500/30 rounded-full blur-xl animate-pulse" />
          <div className="absolute bottom-20 right-20 w-32 h-32 bg-cyan-500/30 rounded-full blur-xl animate-pulse" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center space-y-8"
          >
            <div className="inline-flex items-center space-x-2 bg-blue-600/20 border border-blue-500/30 rounded-full px-4 py-2 text-sm text-blue-300">
              <SafeIcon icon={FiPhone} className="text-lg" />
              <span>Get in Touch</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold">
              <span className="gradient-text">Contact Us Today</span>
              <br />
              <span className="text-white">For Expert Service</span>
            </h1>

            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Ready to get started? Contact our team for professional electrical and automation solutions.
              We're here to help with all your service needs.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:border-blue-500/50 transition-all duration-500 text-center"
              >
                <div className="mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300 glow-blue">
                    <SafeIcon
                      icon={info.icon}
                      className={`text-2xl ${info.color}`}
                    />
                  </div>
                </div>

                <h3 className="text-lg font-bold text-white mb-3">{info.title}</h3>
                <div className="space-y-1 mb-4">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="text-gray-400 text-sm">{detail}</p>
                  ))}
                </div>

                <a
                  href={info.link}
                  className={`inline-flex items-center space-x-2 text-sm font-medium ${info.color} hover:text-white transition-colors duration-300`}
                >
                  <span>{info.action}</span>
                </a>
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50">
                <h2 className="text-3xl font-bold text-white mb-6">
                  <span className="gradient-text">Send us a Message</span>
                </h2>

                {isSubmitted && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-green-600/20 border border-green-500/30 rounded-lg p-4 mb-6 flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiCheck} className="text-green-400" />
                    <span className="text-green-300">Message sent successfully! We'll contact you soon.</span>
                  </motion.div>
                )}

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-600/20 border border-red-500/30 rounded-lg p-4 mb-6"
                  >
                    <span className="text-red-300">{error}</span>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors duration-300"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">Phone / WhatsApp *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors duration-300"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">Service Required *</label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors duration-300"
                    >
                      <option value="">Select a service</option>
                      {services.map((service, index) => (
                        <option key={index} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">Message</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors duration-300 resize-none"
                      placeholder="Tell us about your requirements..."
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <SafeIcon icon={FiSend} className="text-lg" />
                    <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                  </button>
                </form>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50">
                <h3 className="text-2xl font-bold text-white mb-4"><span className="gradient-text">Our Location</span></h3>
                <div className="aspect-video bg-gray-700/50 rounded-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3307.123456789!2d74.3436!3d34.2091!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDEyJzMyLjgiTiA3NMKwMjAnMzcuMCJF!5e0!3m2!1sen!2sin!4v1234567890123"
                    width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Tayyab Electric Studio Location"
                  />
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-600/10 to-orange-600/10 backdrop-blur-sm rounded-2xl p-8 border border-red-500/30">
                <h3 className="text-2xl font-bold text-white mb-4"><span className="text-red-400">Emergency Service</span></h3>
                <p className="text-gray-300 mb-6">Need immediate electrical assistance? Our emergency service is available 24/7 for urgent repairs and safety issues.</p>
                <a
                  href="tel:+919906669918"
                  className="inline-flex items-center space-x-3 bg-gradient-to-r from-red-600 to-orange-600 px-6 py-3 rounded-full text-white font-semibold hover:from-red-700 hover:to-orange-700 transition-all duration-300"
                >
                  <SafeIcon icon={FiPhone} className="text-lg" />
                  <span>Emergency Call</span>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;